import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
// import { SMAAPass } from "https://cdn.jsdelivr.net/npm/three@0.132.0/examples/jsm/postprocessing/SMAAPass.js"

// usdz : usdc, usd, usda
// import { USDZLoader } from 'three-usdz-loader';
//usdz : only usda
import { USDZLoader } from './USDZLoader';
import { calculateCuratorTilingForTexture, inchesToMeters } from '../helpers/jsHelper';
import { THREEJS_TEXTURE_HEIGHT, THREEJS_TEXTURE_WIDTH } from '../config/constant/projectConstant';

const Model3DLoader = forwardRef((props, ref) => {
  if (!props?.url || !props?.extension) return null;
  return <Load3DModel {...props} ref={ref} />;
});

export default Model3DLoader;

const Load3DModel = forwardRef(
  (
    {
      url,
      extension,
      setModelLoading,
      setError,
      applyTexture = false,
      selectedItem = null,
      style = {},
      autoRotate = false,
      showDimension = false,
      dimensions = {},
    },
    ref
  ) => {
    let textMeshes = [];
    const [dimension, setDimension] = useState({ width: 510, height: 480 });
    const controlsRef = useRef(null); // Store controls reference

    const appliedTextureOnParts = useMemo(
      () =>
        selectedItem?.texture_page_changed_product_parts
          ? selectedItem?.texture_page_changed_product_parts
              ?.split(',')
              ?.map((a) => a?.toString()?.trim())
          : [],
      [selectedItem]
    );

    const gap = 0.1;
    const textWidthGap = 0.1;
    const textHeightGap = 0.1;
    const textDepthGap = 0.1;

    // Helper function for rotation
    const setRotation = (x, y, z) => {
      return [(Math.PI / 180) * x, (Math.PI / 180) * y, (Math.PI / 180) * z];
    };

    const measureToolProps = useMemo(
      () => ({
        cap_t: '0.002',
        cap_e: '0.002',
        height: inchesToMeters(dimensions?.height), // in meters
        width: inchesToMeters(dimensions?.width), // in meters
        depth: inchesToMeters(dimensions?.depth), // in meters
        textHeight: `${dimensions?.height} Inches`, // in inches
        textWidth: `${dimensions?.width} Inches`, // in inches
        textDepth: `${dimensions?.depth} Inches`, // in inches
      }),
      [dimensions]
    );

    // Create cylinders for height, width, depth
    const createMeasurementCylinders = ({ scene }) => {
      const createCylinder = (position, rotation, radiusTop, radiusBottom, height) => {
        const geometry = new THREE.CylinderGeometry(radiusTop, radiusBottom, height, 16);
        const material = new THREE.MeshStandardMaterial({
          color: 0x2B2A28,
          visible: showDimension,
        });
        window.measurementLabelMaterial = [...(window.measurementLabelMaterial || []), material];
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(...position);
        mesh.rotation.set(...setRotation(...rotation));
        scene.add(mesh);
      };

      const { width, height, depth, cap_t, cap_e } = measureToolProps;

      // Height
      createCylinder(
        [-width / 2 - gap, height / 2, -depth / 2 - gap],
        [0, 0, 0],
        cap_t,
        cap_e,
        height
      );
      createCylinder(
        [-width / 2 - gap / 1.2, height, -depth / 2 - gap / 1.2],
        [90, 0, -45],
        cap_t,
        cap_e,
        gap / 2
      );
      createCylinder(
        [-width / 2 - gap / 1.2, 0, -depth / 2 - gap / 1.2],
        [90, 0, -45],
        cap_t,
        cap_e,
        gap / 2
      );

      // Width
      createCylinder([0, 0, depth / 2 + gap], [90, 0, 90], cap_t, cap_e, width);
      createCylinder([-width / 2, 0, depth / 2 + 0.75 * gap], [90, 0, 0], cap_t, cap_e, gap / 2);
      createCylinder([width / 2, 0, depth / 2 + 0.75 * gap], [90, 0, 0], cap_t, cap_e, gap / 2);

      // Depth
      createCylinder([-width / 2 - gap, 0, 0], [90, 0, 0], cap_t, cap_e, depth);
      createCylinder([-width / 2 - 0.75 * gap, 0, -depth / 2], [90, 0, 90], cap_t, cap_e, gap / 2);
      createCylinder([-width / 2 - 0.75 * gap, 0, depth / 2], [90, 0, 90], cap_t, cap_e, gap / 2);
    };

    // Create Word (Text) Labels
    const createWordLabel = ({ axis = 'X', title, position, rotation, scene, camera }) => {
      const loader = new THREE.FontLoader();

      const fonts = 'https://threejs.org/examples/fonts/helvetiker_regular.typeface.json';

      loader.load(fonts, (font) => {
        const geometry = new THREE.TextGeometry(title, {
          font: font,
          size: 0.03,
          height: 0,
        });
        const material = new THREE.MeshStandardMaterial({
          color: 0x2B2A28,
          depthTest: false,
          visible: showDimension,
        });
        window.wordLabelMaterial = [...(window.wordLabelMaterial || []), material];
        const mesh = new THREE.Mesh(geometry, material);

        // Compute the bounding box of the text
        geometry.computeBoundingBox();
        const textWidth = geometry.boundingBox.max.x - geometry.boundingBox.min.x;
        const textHeight = geometry.boundingBox.max.y - geometry.boundingBox.min.y;
        const textDepth = geometry.boundingBox.max.z - geometry.boundingBox.min.z; // Handle Z dimension if needed

        // Adjust the position to center the text
        mesh.position.set(
          position[0] - textWidth / 2, // Center on X-axis
          position[1] - textHeight / 2, // Center on Y-axis
          position[2] - textDepth / 2 // Center on Z-axis if needed (optional, based on your use case)
        );

        // Apply rotation to the text mesh
        mesh.rotation.set(...setRotation(...rotation));

        // Rotate text to maintain vertical alignment
        if (axis === 'Z') {
          // mesh.rotation.z = Math.PI / 2;
        } else if (axis === 'Y') {
          // mesh.rotation.y = Math.PI / 2;
        } else {
          // mesh.rotation.x = 0
        }

        scene.add(mesh);
        // mesh.lookAt(camera.position); // Ensure text faces the camera
        // textMeshes.push(mesh);
      });
    };

    useEffect(() => {
      const scene = new THREE.Scene();
      const loader = new THREE.CubeTextureLoader();
      const textureCube = loader.load([
        '/envmap/fishermans_bastion/posx.jpg',
        '/envmap/fishermans_bastion/negx.jpg',
        '/envmap/fishermans_bastion/posy.jpg',
        '/envmap/fishermans_bastion/negy.jpg',
        '/envmap/fishermans_bastion/posz.jpg',
        '/envmap/fishermans_bastion/negz.jpg',
      ]);
      // scene.environment = textureCube; // Useful for reflective materials

      const hdrLoader = new RGBELoader();
      hdrLoader.load('/envmap/hdri/forest_slope_1k.hdr', (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping; // Use reflection mapping for environment maps

        // Set the environment map for the scene
        scene.environment = texture;

        // Optionally set the background
        scene.background = false;

        // Adjust intensity if required
        renderer.toneMappingExposure = 0.4; // Equivalent to environmentIntensity in R3F
      });

      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 512;
      const context = canvas.getContext('2d');
      // Create a linear gradient (vertical)
      const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#f5f5f5'); // Top color (blue)
      gradient.addColorStop(1, ' #e0e0e0'); // Bottom color (light blue)
      // Apply the gradient to the canvas
      context.fillStyle = gradient;
      context.fillRect(0, 0, canvas.width, canvas.height);
      // Use the canvas as a texture
      const texture = new THREE.CanvasTexture(canvas);
      // Set the texture as the scene's background
      scene.background = texture;

      const camera = new THREE.PerspectiveCamera(
        75,
        dimension?.width / dimension?.height,
        0.1,
        10000
      );

      // Set Camera Position for Initial Rotation (45-degree angle)
      const radius = 60; // Distance from the origin
      const angle = THREE.MathUtils.degToRad(-25); // Convert 45 degrees to radians

      // Use polar coordinates for positioning
      const x = radius * Math.sin(angle); // Horizontal offset
      // const y = radius * Math.sin(angle) // Vertical offset
      const z = radius * Math.cos(angle); // Depth offset

      camera.position.set(x, 0, z); // Set camera position at a 45-degree angle
      camera.lookAt(0, 0, 0);
      // camera.position.set(0, 0, 50)

      const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
      renderer.setSize(dimension?.width, dimension?.height);
      renderer.outputEncoding = THREE.sRGBEncoding; // Correct color space encoding
      renderer.toneMapping = THREE.ACESFilmicToneMapping; // Tone mapping for photorealistic colors
      renderer.toneMappingExposure = 1.2; // Adjust exposure if needed
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setClearColor(0xffffff, 1);

      //post processing
      const composer = new EffectComposer(renderer);
      const renderPass = new RenderPass(scene, camera);
      composer.addPass(renderPass);

      ref?.current?.appendChild(renderer.domElement);

      window.THREE_CANVAS = renderer; // Store it for later use

      // var ambientLight = new THREE.AmbientLight(0x888888, 0.25);
      // var directionalLight = new THREE.DirectionalLight(0x888888, 0.25);
      // scene.add(directionalLight);
      // scene.add(ambientLight);

      // Add a directional light
      const directionalLight = new THREE.DirectionalLight(0xffffff, 2); // Bright, dramatic light
      directionalLight.position.set(5, 10, 5); // Position the light high above and to the side
      directionalLight.castShadow = true; // Enable shadows for the light
      scene.add(directionalLight);

      // Add an ambient light for slight ambient visibility (optional)
      const ambientLight = new THREE.AmbientLight(0x222222); // Dim ambient light
      scene.add(ambientLight);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;

      controlsRef.current = controls; // Save controls reference
      controls.autoRotate = autoRotate; // setting initial autoRotate value
      controls.autoRotateSpeed = 1;

      const updateDimension = () => {
        if (!ref?.current) return;

        const width = ref?.current?.clientWidth;
        let height = ref?.current?.clientHeight;

        // if (width > 510) height = 510;
        // if (height > 480) height = 480;

        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);

        window.THREE_CANVAS = renderer; // Store it for later use

        render();

        setDimension({ width, height });
      };

      // Function to resize a texture using a canvas while preserving color fidelity
      function resizeTexture(texture, targetWidth, targetHeight) {
        console.time(`Resizing texture for ${texture || 'unknown'}`);

        // Create a canvas with the target resolution
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const context = canvas.getContext('2d');

        // Adjust color space if the texture uses sRGB encoding
        if (texture.encoding === THREE.sRGBEncoding) {
          context.filter = 'brightness(1) contrast(1)';
        }

        // Draw the original texture onto the canvas
        context.drawImage(texture.image, 0, 0, targetWidth, targetHeight);

        // Create a new texture from the resized canvas
        const resizedTexture = new THREE.Texture(canvas);
        resizedTexture.needsUpdate = true;

        // Preserve the encoding and mapping of the original texture
        resizedTexture.encoding = texture.encoding;
        resizedTexture.mapping = texture.mapping;

        // Ensure correct wrapping and filtering settings
        resizedTexture.wrapS = texture.wrapS;
        resizedTexture.wrapT = texture.wrapT;
        resizedTexture.minFilter = texture.minFilter;
        resizedTexture.magFilter = texture.magFilter;

        console.timeEnd(`Resizing texture for ${texture || 'unknown'}`);
        return resizedTexture;
      }

      const loadModel = async () => {
        let loader;
        if (extension === 'fbx') {
          loader = new FBXLoader();
        } else if (extension === 'obj') {
          loader = new OBJLoader();
        } else if (extension === 'glb' || extension === 'gltf') {
          loader = new GLTFLoader();
          const draco = new DRACOLoader();
          draco.setDecoderPath('/draco/'); // getting the draco files from the public folder
          draco.setDecoderConfig({ type: 'js' });

          loader.setDRACOLoader(draco);
        } else if (extension === 'usdz') {
          // usda support
          loader = new USDZLoader();
          //usdc & usd support
          // const usdzloader = new USDZLoader();
          // setModelLoading?.(true)
          // console.log('extension ______________1 : ', url);
          // let object = new THREE.Group();
          // let blob = await fetch(url).then((r) => r.blob());
          // console.log('blob loaded successfully ______________2 : ', blob);
          // usdzloader.loadFile(new File([blob], 'name.usdz'), object).then(
          //   (usdzobj) => {
          //     console.log('loaded successfully ______________2 : ', object);
          //     object.position.set(0, 0, 0);
          //     scene.add(object);
          //     fitCameraToSelection(camera, controls, [object], 1.15);
          //     setModelLoading?.(false)
          //     // animate();
          //   },
          //   (err) => {
          //     console.log(err);
          //   }
          // );
        } else {
          setModelLoading?.(false);
          setError(true);
          return;
        }
        setModelLoading?.(true);
        loader?.load(
          url,
          (obj) => {
            let object;
            if (extension === 'glb' || extension === 'gltf' || obj.scene) {
              object = obj.scene;
            } else {
              object = obj;
            }

            if(dimensions?.width && dimensions?.height && dimensions?.depth) {
              createMeasurementCylinders({ scene });

              const { textHeight, textWidth, textDepth, depth, width, height } = measureToolProps;

              // Add text labels for dimensions
              createWordLabel({
                axis: 'X',
                title: textWidth,
                position: [0, textWidthGap / 2, depth / 2 + textWidthGap],
                rotation: [-45, 0, 0],
                scene,
                camera,
              });

              createWordLabel({
                axis: 'Y',
                title: textHeight,
                position: [
                  -width / 2 - gap / 1.5,
                  height / 2 - textHeightGap / 2,
                  -depth / 2 - gap / 5,
                ],
                rotation: [0, -45, 90],
                scene,
                camera,
              });
              createWordLabel({
                axis: 'Z',
                title: textDepth,
                position: [-width / 2 - gap / 5, 0.05, 0 - textDepthGap],
                rotation: [-90, -45, -90],
                scene,
                camera,
              });
            }

            object.position.set(0, 0, 0);
            // console.log('object data ', obj);

            object.traverse(async function (child) {
              if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                // console.log('DEBUG__child', child);
                if (child.material.map) {
                  // const { width, height } = child.material.map.image;
                  // if (width > THREEJS_TEXTURE_WIDTH || height > THREEJS_TEXTURE_HEIGHT) {
                  //   console.log(
                  //     `Resizing texture for ${child.name} from ${width}x${height} to ${THREEJS_TEXTURE_WIDTH}x${THREEJS_TEXTURE_HEIGHT}`
                  //   );
                  //   const resizedTexture = resizeTexture(
                  //     child.material.map,
                  //     THREEJS_TEXTURE_WIDTH,
                  //     THREEJS_TEXTURE_HEIGHT
                  //   );
                  //   // Apply wrapping and tiling to the resized texture
                  //   resizedTexture.wrapS = THREE.RepeatWrapping;
                  //   resizedTexture.wrapT = THREE.RepeatWrapping;
                  //   // Optionally, set tiling values (1x1 default)
                  //   resizedTexture.repeat.set(1, 1);
                  //   // Replace the original texture with the resized version
                  //   child.material.map = resizedTexture;
                  //   child.material.needsUpdate = true;
                  // }
                } else if (!child.material) {
                  child.material = new THREE.MeshPhongMaterial({
                    color: 0xb5b5b5,
                  });
                  // console.log('No texture map found for mesh:', child);
                } else {
                  // child.material = new THREE.MeshPhongMaterial({
                  //   color: 0xb5b5b5,
                  // });
                  // console.log('No texture map found for mesh:', child);
                }
                if (extension !== 'obj') {
                  child.material.envMap = textureCube;
                }

                // applying texture on the model for the product page
                if (applyTexture && appliedTextureOnParts.includes(child.name) && selectedItem) {
                  //calculating tiling
                  const { width, height, texture_file } = selectedItem || {};
                  const tilingX = calculateCuratorTilingForTexture(width);
                  const tilingY = calculateCuratorTilingForTexture(height);
                  // console.log('DEBUG___tilingValue', {
                  //   tilingX,
                  //   tilingY,
                  //   width,
                  //   height,
                  // });
                  //***************** */

                  if (texture_file) {
                    const loader = new THREE.TextureLoader();
                    // load a resource
                    loader.load(
                      // resource URL
                      texture_file,

                      // onLoad callback
                      function (texture) {
                        const { width, height } = texture.image;

                        let resizedTexture = texture;
                        if (width > THREEJS_TEXTURE_WIDTH || height > THREEJS_TEXTURE_HEIGHT) {
                          console.log(
                            `Resizing loaded texture for ${child.name} from ${width}x${height} to ${THREEJS_TEXTURE_WIDTH}x${THREEJS_TEXTURE_HEIGHT}`
                          );
                          resizedTexture = resizeTexture(
                            texture,
                            THREEJS_TEXTURE_WIDTH,
                            THREEJS_TEXTURE_HEIGHT
                          );
                        }
                        // in this example we create the material when the texture is loaded
                        // Set the wrapping mode to RepeatWrapping to allow tiling

                        resizedTexture.wrapS = THREE.RepeatWrapping;
                        resizedTexture.wrapT = THREE.RepeatWrapping;

                        // Set the tiling values
                        resizedTexture.repeat.set(tilingX, -tilingY);

                        // Set the offset values
                        const offsetX = 0; // Example offset value along X-axis
                        const offsetY = tilingY; // Example offset value along Y-axis
                        resizedTexture.offset.set(offsetX, offsetY);

                        child.material.map = resizedTexture;
                        child.material.needsUpdate = true;
                        setModelLoading?.(false);
                      },

                      // onProgress callback currently not supported
                      undefined,

                      // onError callback
                      function (err) {
                        console.error('An error happened.');
                      }
                    );
                  }
                }
                child.material.needsUpdate = true;
                // console.log('Material ', child.material);
              }
            });

            // console.log('DEBUG___scene', {
            //   scene,
            //   camera,
            // });

            // Hide the model until its textures finish uploading to the GPU.
            // Otherwise FBX meshes flash black for a few frames because the
            // spinner overlay doesn't fully cover the canvas behind it.
            object.visible = false;

            scene.add(object);
            fitCameraToSelection(camera, controls, [object], 1.5);
            animate();

            // Force shader compilation + texture uploads synchronously.
            if (typeof renderer.compile === 'function') {
              renderer.compile(scene, camera);
            }
            render();

            // Wait a few frames so the GPU has actually consumed the uploads,
            // then reveal the model and hide the loader.
            requestAnimationFrame(() => {
              render();
              requestAnimationFrame(() => {
                render();
                requestAnimationFrame(() => {
                  object.visible = true;
                  render();
                  if (!applyTexture) {
                    setTimeout(() => setModelLoading?.(false), 300);
                  } else {
                    setModelLoading?.(applyTexture);
                  }
                });
              });
            });
            // console.log('DEBUG___object', object);
          },
          (xhr) => {
            // setModelLoading?.((xhr.loaded / xhr.total) * 100 !== 100);
            // console.log((xhr.loaded / xhr.total) * 100 + '% loaded');
          },
          (error) => {
            console.log(error);
            setModelLoading?.(false);
            setError(true);
          }
        );
      };

      function onWindowResize() {
        updateDimension();
      }

      function animate() {
        requestAnimationFrame(animate);
        controls?.update();
        controlsRef.current.update(); // Always update controls

        directionalLight.position.copy(camera.position);
        directionalLight.rotation.copy(camera.rotation);
        textMeshes.forEach((textMesh) => {
          textMesh.lookAt(camera.position);
        });
        render();
      }
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      const box = new THREE.Box3();

      function fitCameraToSelection(camera, controls, selection, fitOffset = 1.2) {
        if (camera.fov > 25) {
          camera.fov = 25;
        }
        box.makeEmpty();
        for (const object of selection) {
          box.expandByObject(object);
        }

        box.getSize(size);
        box.getCenter(center);

        const maxSize = Math.max(size.x, size.y, size.z);
        const fitHeightDistance = maxSize / (2 * Math.atan((Math.PI * camera.fov) / 360));
        const fitWidthDistance = fitHeightDistance / camera.aspect;
        const distance = fitOffset * Math.max(fitHeightDistance, fitWidthDistance);

        const direction = controls.target
          .clone()
          .sub(camera.position)
          .normalize()
          .multiplyScalar(distance);

        controls.maxDistance = distance * 10;
        controls.target.copy(center);

        camera.near = distance / 100;
        camera.far = distance * 100;
        camera.updateProjectionMatrix();

        camera.position.copy(controls.target).sub(direction);

        controls.update();
      }

      function render() {
        // renderer.render(scene, camera);
        composer.render();
      }

      animate();

      loadModel();

      if (ref?.current) {
        updateDimension();
      }

      window.addEventListener('resize', onWindowResize, false);

      function destroyScene(scene, renderer) {
        // Traverse the scene and dispose of resources
        scene?.traverse((object) => {
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => {
                if (material.map) material.map.dispose();
                material.dispose();
              });
            } else {
              if (object.material.map) object.material.map.dispose();
              object.material.dispose();
            }
          }
        });

        // Dispose of the renderer and its WebGL context
        renderer?.dispose();

        // Remove the canvas element
        const canvas = renderer.domElement;
        if (canvas && canvas.parentElement) {
          canvas?.parentElement?.removeChild?.(canvas);
        }

        // Clear the scene's children
        scene?.clear();
      }

      return () => {
        window.THREE_CANVAS = null;

        destroyScene(scene, renderer);
        setModelLoading?.(false);
        window.removeEventListener('resize', onWindowResize, false);
        // Before removing, check if the element is actually a child
        if (ref?.current?.contains(renderer.domElement)) {
          ref?.current?.removeChild?.(renderer.domElement);
        }
      };
    }, [url, extension, ref]);

    // Update autoRotate dynamically WITHOUT resetting the scene
    useEffect(() => {
      if (controlsRef.current) {
        controlsRef.current.autoRotate = autoRotate; // Toggle autoRotate smoothly
      }
    }, [autoRotate]);

    useEffect(() => {
      if (window?.measurementLabelMaterial?.length > 0) {
        window.measurementLabelMaterial.forEach((m) => {
          m.visible = showDimension;
        });
      }
      if (window?.wordLabelMaterial?.length > 0) {
        window.wordLabelMaterial.forEach((m) => {
          m.visible = showDimension;
        });
      }
    }, [showDimension]);

    return (
      <div
        ref={ref}
        style={{
          width: '100%',
          height: '100%',
          ...style,
        }}
      />
    );
  }
);
